import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Snackbar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Fade,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  Share,
  ContentCopy,
  Person,
  AttachMoney,
  Star,
  EmojiEvents,
  Group,
  CheckCircle,
  Launch,
  WhatsApp,
  Email,
  Facebook,
  Twitter,
  PaymentOutlined,
  AccountBalanceWallet,
  History,
  Cancel,
  Schedule,
  Error,
  MonetizationOn,
  Timeline,
  TrendingUp,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { referralService } from '../services/referralService';

const Referrals = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Existing states
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [applyCodeMode, setApplyCodeMode] = useState(false);
  const [enteredCode, setEnteredCode] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  
  // New PayPal states
  const [currentTab, setCurrentTab] = useState(0);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [referralsList, setReferralsList] = useState([]);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [paypalEmailDialogOpen, setPaypalEmailDialogOpen] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth');
        return;
      }
      await loadReferralData();
    };
    
    checkAuth();
  }, [navigate]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      
      // Load referral code
      const codeResult = await referralService.getMyReferralCode();
      if (codeResult.success) {
        setReferralCode(codeResult.referralCode);
        setShareUrl(referralService.generateShareUrl(codeResult.referralCode));
      }

      // Load stats
      const statsResult = await referralService.getReferralStats();
      if (statsResult.success) {
        setStats(statsResult);
      }

      // Load leaderboard
      const leaderboardResult = await referralService.getReferralLeaderboard();
      if (leaderboardResult.success) {
        setLeaderboard(leaderboardResult.leaderboard);
      }

      // Load PayPal payout summary
      await loadPayoutSummary();
      
      // Load payout history
      await loadPayoutHistory();
      
      // Load referrals list
      await loadReferralsList();
      
    } catch (error) {
      console.error('Error loading referral data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load referral data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPayoutSummary = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://backend.sanityreps.com'}/api/paypal/summary`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setPayoutSummary(result.data);
        setPaypalEmail(result.data.paypal_email || '');
      }
    } catch (error) {
      console.warn('Error loading payout summary:', error);
    }
  };

  const loadPayoutHistory = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://backend.sanityreps.com'}/api/paypal/history?limit=20`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setPayoutHistory(result.data || []);
      }
    } catch (error) {
      console.warn('Error loading payout history:', error);
    }
  };

  const loadReferralsList = async () => {
    try {
      const result = await referralService.getReferralsList();
      if (result.success) {
        setReferralsList(result.referrals || []);
      }
    } catch (error) {
      console.warn('Error loading referrals list:', error);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setSnackbar({
        open: true,
        message: 'Referral code copied to clipboard!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to copy code',
        severity: 'error'
      });
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnackbar({
        open: true,
        message: 'Referral link copied to clipboard!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to copy link',
        severity: 'error'
      });
    }
  };

  const handleShare = async () => {
    const result = await referralService.shareReferral(referralCode);
    if (result.success) {
      if (result.method === 'clipboard') {
        setSnackbar({
          open: true,
          message: 'Referral message copied to clipboard!',
          severity: 'success'
        });
      }
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to share',
        severity: 'error'
      });
    }
  };

  const handleApplyCode = async () => {
    if (!enteredCode.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a referral code',
        severity: 'warning'
      });
      return;
    }

    setApplyLoading(true);
    try {
      const result = await referralService.applyReferralCode(enteredCode.trim().toUpperCase());
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Referral code applied successfully!',
          severity: 'success'
        });
        setEnteredCode('');
        setApplyCodeMode(false);
        await loadReferralData(); // Refresh data
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to apply referral code',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to apply referral code',
        severity: 'error'
      });
    } finally {
      setApplyLoading(false);
    }
  };

  const handleSocialShare = (platform) => {
    const text = encodeURIComponent(`Join me on Sanity Reps - your AI wellness companion! Use my referral code ${referralCode} to get started.`);
    const url = encodeURIComponent(shareUrl);
    
    let shareUrl_platform;
    switch (platform) {
      case 'whatsapp':
        shareUrl_platform = `https://wa.me/?text=${text} ${url}`;
        break;
      case 'facebook':
        shareUrl_platform = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
        break;
      case 'twitter':
        shareUrl_platform = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'email':
        shareUrl_platform = `mailto:?subject=Join me on Sanity Reps&body=${text} ${url}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl_platform, '_blank');
  };

  // PayPal handler functions
  const handleUpdatePaypalEmail = async () => {
    try {
      if (!paypalEmail || !paypalEmail.includes('@')) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid PayPal email address',
          severity: 'error'
        });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://backend.sanityreps.com'}/api/paypal/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paypal_email: paypalEmail })
      });

      const result = await response.json();
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'PayPal email updated successfully!',
          severity: 'success'
        });
        setPaypalEmailDialogOpen(false);
        await loadPayoutSummary();
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to update PayPal email',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating PayPal email:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update PayPal email',
        severity: 'error'
      });
    }
  };

  const handleRequestPayout = async () => {
    try {
      setPayoutLoading(true);
      
      const amount = parseFloat(payoutAmount);
      if (isNaN(amount) || amount < 30) {
        setSnackbar({
          open: true,
          message: 'Minimum payout amount is $30.00',
          severity: 'error'
        });
        return;
      }

      if (amount > (payoutSummary?.pending_payouts || 0)) {
        setSnackbar({
          open: true,
          message: 'Insufficient balance for payout',
          severity: 'error'
        });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://backend.sanityreps.com'}/api/paypal/request-payout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          amount: amount,
          notes: payoutNotes 
        })
      });

      const result = await response.json();
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Payout request submitted successfully!',
          severity: 'success'
        });
        setPayoutDialogOpen(false);
        setPayoutAmount('');
        setPayoutNotes('');
        await loadPayoutSummary();
        await loadPayoutHistory();
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to request payout',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error requesting payout:', error);
      setSnackbar({
        open: true,
        message: 'Failed to request payout',
        severity: 'error'
      });
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleCancelPayout = async (requestId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://backend.sanityreps.com'}/api/paypal/cancel/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Payout request cancelled successfully',
          severity: 'success'
        });
        await loadPayoutHistory();
        await loadPayoutSummary();
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to cancel payout',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error cancelling payout:', error);
      setSnackbar({
        open: true,
        message: 'Failed to cancel payout',
        severity: 'error'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'pending':
        return <Schedule color="warning" />;
      case 'processing':
        return <CircularProgress size={20} />;
      case 'failed':
      case 'cancelled':
        return <Error color="error" />;
      default:
        return <Schedule color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Mobile-Optimized Header */}
      <Box sx={{ mb: { xs: 2, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 1, md: 2 },
          flexWrap: 'wrap'
        }}>
          <IconButton 
            onClick={() => navigate('/settings')} 
            sx={{ 
              mr: { xs: 1, md: 2 }, 
              color: '#1976d2',
              p: { xs: 1, md: 1.5 }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            color="primary"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              lineHeight: 1.2
            }}
          >
            Refer Friends & Earn Rewards
          </Typography>
        </Box>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            maxWidth: 600,
            fontSize: { xs: '0.875rem', md: '1rem' },
            lineHeight: 1.5,
            px: { xs: 1, md: 0 }
          }}
        >
          Share Sanity Reps with friends and family. Earn $3 + 1000 XP for each friend who subscribes!
        </Typography>
      </Box>

      {/* Mobile-Optimized Tabs */}
      <Paper sx={{ mb: { xs: 2, md: 3 }, mx: { xs: -1, md: 0 } }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minHeight: isMobile ? 48 : 56,
              fontSize: isMobile ? '0.75rem' : '1rem',
              px: { xs: 1, md: 3 },
              minWidth: { xs: 'auto', md: 120 }
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          <Tab 
            icon={<Share />} 
            label="Share & Earn" 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          />
          <Tab 
            icon={<AccountBalanceWallet />} 
            label="Payouts" 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          />
          <Tab 
            icon={<Group />} 
            label="My Referrals" 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          />
          <Tab 
            icon={<Timeline />} 
            label="Leaderboard" 
            iconPosition="start"
            sx={{ flexDirection: 'row', gap: 1 }}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Your Referral Code */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              mx: { xs: -1, md: 0 }
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  flexWrap: 'wrap'
                }}>
                  <Share sx={{ color: 'white', mr: 1, fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    fontWeight="bold" 
                    color="white"
                    sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                  >
                    Your Referral Code
                  </Typography>
                </Box>
                
                <Paper 
                  sx={{ 
                    p: { xs: 1.5, md: 2 }, 
                    mb: 2, 
                    bgcolor: 'rgba(255,255,255,0.9)',
                    textAlign: 'center',
                    borderRadius: 2
                  }}
                >
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    fontWeight="bold" 
                    color="primary" 
                    letterSpacing={{ xs: 1, md: 2 }}
                    sx={{ 
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                      wordBreak: 'break-all'
                    }}
                  >
                    {referralCode}
                  </Typography>
                </Paper>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  mb: 2, 
                  flexDirection: isMobile ? 'column' : 'row' 
                }}>
                  <Button
                    variant="contained"
                    onClick={handleCopyCode}
                    startIcon={<ContentCopy sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Copy Code
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleShare}
                    startIcon={<Share sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Share
                  </Button>
                </Box>

                <Typography 
                  variant="body2" 
                  color="rgba(255,255,255,0.8)" 
                  textAlign="center"
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Share this code or use the buttons below to invite friends
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Referral Stats */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              height: '100%',
              mx: { xs: -1, md: 0 }
            }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  flexWrap: 'wrap'
                }}>
                  <EmojiEvents sx={{ 
                    color: '#f59e0b', 
                    mr: 1,
                    fontSize: { xs: '1.2rem', md: '1.5rem' }
                  }} />
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                  >
                    Your Achievements
                  </Typography>
                </Box>

                <Grid container spacing={{ xs: 1, md: 2 }}>
                  <Grid item xs={6}>
                    <Paper sx={{ 
                      p: { xs: 1.5, md: 2 }, 
                      textAlign: 'center', 
                      bgcolor: '#f0f9ff',
                      borderRadius: 2
                    }}>
                      <Group sx={{ 
                        color: '#0ea5e9', 
                        fontSize: { xs: 24, sm: 28, md: 32 }, 
                        mb: 1 
                      }} />
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        fontWeight="bold" 
                        color="primary"
                        sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                      >
                        {stats?.totalReferrals || 0}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                      >
                        Friends Referred
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Paper sx={{ 
                      p: { xs: 1.5, md: 2 }, 
                      textAlign: 'center', 
                      bgcolor: '#f0fdf4',
                      borderRadius: 2
                    }}>
                      <MonetizationOn sx={{ 
                        color: '#10b981', 
                        fontSize: { xs: 24, sm: 28, md: 32 }, 
                        mb: 1 
                      }} />
                      <Typography 
                        variant={isMobile ? "h6" : "h5"} 
                        fontWeight="bold" 
                        color="#10b981"
                        sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                      >
                        ${stats?.totalEarnings || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Earned
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6}>
                    <Paper sx={{ p: isMobile ? 1.5 : 2, textAlign: 'center', bgcolor: '#fef3c7' }}>
                      <Star sx={{ color: '#f59e0b', fontSize: isMobile ? 28 : 32, mb: 1 }} />
                      <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="#f59e0b">
                        #{stats?.rank || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your Rank
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6}>
                    <Paper sx={{ p: isMobile ? 1.5 : 2, textAlign: 'center', bgcolor: '#fdf2f8' }}>
                      <TrendingUp sx={{ color: '#ec4899', fontSize: isMobile ? 28 : 32, mb: 1 }} />
                      <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="#ec4899">
                        {stats?.successRate || 0}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Success Rate
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* How It Works - Mobile Optimized */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mx: { xs: -1, md: 0 } }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  fontWeight="bold" 
                  mb={2}
                  sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                >
                  How Referrals Work
                </Typography>
                
                <Stack spacing={{ xs: 1.5, md: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: { xs: 1.5, md: 2 },
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <Chip 
                      label="1" 
                      color="primary" 
                      sx={{ 
                        mt: isMobile ? 0 : 0.5, 
                        minWidth: 28,
                        alignSelf: isMobile ? 'flex-start' : 'auto'
                      }} 
                    />
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        Share Your Code
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                      >
                        Share your unique referral code with friends and family
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: { xs: 1.5, md: 2 },
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <Chip 
                      label="2" 
                      color="primary" 
                      sx={{ 
                        mt: isMobile ? 0 : 0.5, 
                        minWidth: 28,
                        alignSelf: isMobile ? 'flex-start' : 'auto'
                      }} 
                    />
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        Friend Subscribes
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                      >
                        When they sign up and subscribe to any paid plan
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: { xs: 1.5, md: 2 },
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <Chip 
                      label="3" 
                      color="primary" 
                      sx={{ 
                        mt: isMobile ? 0 : 0.5, 
                        minWidth: 28,
                        alignSelf: isMobile ? 'flex-start' : 'auto'
                      }} 
                    />
                    <Box>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        You Earn Rewards
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                      >
                        You earn $3 credit + 1000 XP when they subscribe
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Social Sharing - Mobile Optimized */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mx: { xs: -1, md: 0 } }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  fontWeight="bold" 
                  mb={2}
                  sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                >
                  Quick Share
                </Typography>
                
                <Stack spacing={{ xs: 1, md: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleSocialShare('whatsapp')}
                    startIcon={<WhatsApp sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      justifyContent: 'flex-start',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Share on WhatsApp
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => handleSocialShare('facebook')}
                    startIcon={<Facebook sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      justifyContent: 'flex-start',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Share on Facebook
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => handleSocialShare('twitter')}
                    startIcon={<Twitter sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      justifyContent: 'flex-start',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Share on Twitter
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => handleSocialShare('email')}
                    startIcon={<Email sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      justifyContent: 'flex-start',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Share via Email
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={handleCopyUrl}
                    startIcon={<ContentCopy sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />}
                    fullWidth
                    sx={{ 
                      justifyContent: 'flex-start',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    Copy Link
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Apply Referral Code */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" mb={2}>
                  Have a Referral Code?
                </Typography>
                
                {!applyCodeMode ? (
                  <Button
                    variant="outlined"
                    onClick={() => setApplyCodeMode(true)}
                    fullWidth
                    startIcon={<Person />}
                  >
                    Apply Code
                  </Button>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      label="Enter Referral Code"
                      value={enteredCode}
                      onChange={(e) => setEnteredCode(e.target.value.toUpperCase())}
                      placeholder="e.g., ABC12345"
                      sx={{ mb: 2 }}
                      inputProps={{ maxLength: 8 }}
                      size={isMobile ? "small" : "medium"}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                      <Button
                        variant="outlined"
                        onClick={() => setApplyCodeMode(false)}
                        fullWidth={isMobile}
                        size={isMobile ? "small" : "medium"}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleApplyCode}
                        disabled={!enteredCode || applyLoading}
                        fullWidth={isMobile}
                        size={isMobile ? "small" : "medium"}
                      >
                        {applyLoading ? 'Applying...' : 'Apply'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* PayPal Payouts Tab */}
      {currentTab === 1 && (
        <Grid container spacing={3}>
          {/* Earnings Overview */}
          <Grid item xs={12}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MonetizationOn sx={{ color: 'white', mr: 1 }} />
                  <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="white">
                    Earnings Overview
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)' }}>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        ${payoutSummary?.pending_payouts || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Available Balance
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)' }}>
                      <Typography variant="h4" fontWeight="bold" color="#10b981">
                        ${payoutSummary?.referral_credits || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Earned
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)' }}>
                      <Typography variant="h4" fontWeight="bold" color="#6366f1">
                        ${payoutSummary?.total_payouts || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Paid Out
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* PayPal Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PaymentOutlined sx={{ color: '#0070ba', mr: 1 }} />
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
                    PayPal Settings
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    PayPal Email Address:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {paypalEmail || 'Not set'}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPaypalEmailDialogOpen(true)}
                    >
                      {paypalEmail ? 'Change' : 'Set'}
                    </Button>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Request Payout Requirements:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color={paypalEmail ? "success" : "disabled"} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="PayPal email address set"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color={(payoutSummary?.pending_payouts || 0) >= 30 ? "success" : "disabled"} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Minimum $30.00 balance"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Request Payout */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold" mb={2}>
                  Request Payout
                </Typography>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Available Balance: ${payoutSummary?.pending_payouts || 0}
                </Typography>
                
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setPayoutDialogOpen(true)}
                  disabled={!paypalEmail || (payoutSummary?.pending_payouts || 0) < 30}
                  startIcon={<AccountBalanceWallet />}
                  sx={{ mb: 2 }}
                >
                  Request Payout
                </Button>
                
                {(!paypalEmail || (payoutSummary?.pending_payouts || 0) < 30) && (
                  <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                    {!paypalEmail ? 'Set your PayPal email to request payouts' : 
                     'Minimum $30.00 balance required for payout'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Payout History */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <History sx={{ color: '#6366f1', mr: 1 }} />
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
                    Payout History
                  </Typography>
                </Box>
                
                {payoutHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    No payout requests yet
                  </Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: isMobile ? 300 : 400 }}>
                    <Table stickyHeader size={isMobile ? "small" : "medium"}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                          {!isMobile && <TableCell>PayPal Email</TableCell>}
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payoutHistory.map((payout) => (
                          <TableRow key={payout.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                ${payout.amount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getStatusIcon(payout.status)}
                                label={payout.status}
                                color={getStatusColor(payout.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(payout.requested_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            {!isMobile && (
                              <TableCell>
                                <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {payout.paypal_email}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              {payout.status === 'pending' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleCancelPayout(payout.id)}
                                  color="error"
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* My Referrals Tab */}
      {currentTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Group sx={{ color: '#0ea5e9', mr: 1 }} />
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
                    My Referrals ({referralsList.length})
                  </Typography>
                </Box>
                
                {referralsList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    No referrals yet. Start sharing your code to see your referrals here!
                  </Typography>
                ) : (
                  <TableContainer sx={{ maxHeight: isMobile ? 400 : 500 }}>
                    <Table stickyHeader size={isMobile ? "small" : "medium"}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Friend</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Joined</TableCell>
                          <TableCell>Earned</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referralsList.map((referral, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#667eea' }}>
                                  {referral.email?.charAt(0).toUpperCase() || 'U'}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {referral.email || 'Anonymous'}
                                  </Typography>
                                  {referral.name && (
                                    <Typography variant="caption" color="text.secondary">
                                      {referral.name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={referral.status || 'Active'}
                                color={referral.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {new Date(referral.created_at).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" color="success.main">
                                ${referral.reward_amount || 10}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Leaderboard Tab */}
      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmojiEvents sx={{ color: '#f59e0b', mr: 1 }} />
                  <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
                    Top Referrers
                  </Typography>
                </Box>
                
                {leaderboard.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    Leaderboard will appear once referrals are made
                  </Typography>
                ) : (
                  <List>
                    {leaderboard.slice(0, 10).map((user, index) => (
                      <ListItem key={index} divider={index < leaderboard.length - 1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                          <Badge
                            badgeContent={
                              index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null
                            }
                            sx={{ '& .MuiBadge-badge': { fontSize: '1.2rem' } }}
                          >
                            <Chip 
                              label={index + 1} 
                              size="small" 
                              color={index < 3 ? 'warning' : 'default'}
                              sx={{ 
                                minWidth: 32,
                                fontWeight: 'bold'
                              }}
                            />
                          </Badge>
                          
                          <Avatar sx={{ bgcolor: '#667eea' }}>
                            {user.email?.charAt(0).toUpperCase() || 'U'}
                          </Avatar>
                          
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {user.email || 'Anonymous User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.referrals} referrals • ${user.earnings} earned
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {user.xp || 0} XP
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* PayPal Email Dialog */}
      <Dialog 
        open={paypalEmailDialogOpen} 
        onClose={() => setPaypalEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set PayPal Email Address</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter the email address associated with your PayPal account to receive payouts.
          </Typography>
          <TextField
            fullWidth
            label="PayPal Email"
            type="email"
            value={paypalEmail}
            onChange={(e) => setPaypalEmail(e.target.value)}
            placeholder="your-paypal@email.com"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaypalEmailDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdatePaypalEmail}
            variant="contained"
            disabled={!paypalEmail || !paypalEmail.includes('@')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payout Request Dialog */}
      <Dialog 
        open={payoutDialogOpen} 
        onClose={() => setPayoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Payout</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Available Balance: <strong>${payoutSummary?.pending_payouts || 0}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            PayPal Email: <strong>{paypalEmail}</strong>
          </Typography>
          
          <TextField
            fullWidth
            label="Payout Amount"
            type="number"
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            placeholder="0.00"
            inputProps={{ 
              min: 30, 
              max: payoutSummary?.pending_payouts || 0,
              step: 0.01 
            }}
            sx={{ mt: 2, mb: 2 }}
            helperText={`Minimum $30.00 • Maximum $${payoutSummary?.pending_payouts || 0}`}
          />
          
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={payoutNotes}
            onChange={(e) => setPayoutNotes(e.target.value)}
            placeholder="Additional notes for this payout request..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleRequestPayout}
            variant="contained"
            disabled={payoutLoading || !payoutAmount || parseFloat(payoutAmount) < 30}
          >
            {payoutLoading ? <CircularProgress size={20} /> : 'Request Payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Referrals;
